import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { REAL_VENUE_DATA } from '../../constants/realVenueData';
import { getMetroDistanceText } from '../../services/metroDistanceService';
import discountCodeApiService from '../../services/discountCodeApi';
import BookingForm from './components/BookingForm';
import VenueSidebar from './components/VenueSidebar';
import SuccessModal from './components/SuccessModal';
import venueDashboardApiService from '../../services/venueDashboardApi';
import paymentsApiService from '../../services/paymentsApi';
import { resolvePerPersonRateForBooking, resolveFullVenueDynamicHourlyRate } from '../VenueDashboard/utils/bookingUtils';
import { getYmdInTimezone } from '../VenueDashboard/utils/dateUtils';
import { shouldShowVenuePricing } from '../../utils/venuePricing';
import { isStripePaymentsUiEnabled } from '../../utils/stripeFeatureFlag';
import { computePaymentFeeBreakdown, buildFeeBreakdownSnapshot } from '../../utils/bookingPaymentFees';
import { resolveUserDisplayName } from '../../utils/userDisplayName';
import {
  getAddonPricingUnit,
  resolveAddonLineTotal
} from '../../utils/addonPricing';
import { normalizeTagSlugs, DEFAULT_CATALOG_ADDON_TAGS } from '../../utils/catalogTags';
import { coerceBookingTypeForRules, resolveDefaultBookingType } from '../../utils/bookingPricingMetadata';
import {
  fetchVenueDiscoveryBundle,
  getCachedVenueDiscoveryBundle,
  setCachedVenueDiscoveryBundle,
  venueIdsMatch
} from '../../utils/venueDiscoveryCache';
import venueApiService from '../../services/venueApi';
import { API_BASE_URL } from '../../config/apiEndpoints';
import {
  isVenueUuid,
  resolveVenueIdFromPathParam,
  resolveVenueIdFromSlugSync
} from '../../utils/venueSlug';
import { usePageSeo } from '../Seo/PageSeo';
import './BookingPage.css';

/** Booking phone field shows a +44 prefix and stores the rest as "+44 {local}". */
const toBookingPhoneValue = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (value.startsWith('+') && !value.startsWith('+44')) return value;

  const withoutCode = value.replace(/^\+?44\s*/, '').replace(/^0/, '').trim();
  const digits = withoutCode.replace(/\D/g, '');
  if (!digits) return '';
  return `+44 ${withoutCode}`;
};

const organiserContactFromAccount = (currentUser, userProfile) => {
  if (!currentUser && !userProfile) return null;

  const name = resolveUserDisplayName(userProfile, currentUser);
  const email = String(currentUser?.email || userProfile?.email || '').trim();
  const rawPhone = userProfile?.phone || userProfile?.phone_number || currentUser?.phoneNumber || '';
  const phone = toBookingPhoneValue(rawPhone);

  if (!name && !email && !phone) return null;
  return { name, email, phone };
};

const CLEANING_FEE = 25;

const KASA_VENUE_ID = '660e8400-e29b-41d4-a716-446655440007';

/** Menu order from Kasa's item sheet: sandwiches, pastries, then brownies and blondies. */
const KASA_ITEM_ORDER = [
  'classic caprese',
  'crispy tuna',
  'mediterranean garden',
  'truffle ham',
  'truffle turkey',
  'turkey florentine',
  'almond croissant',
  'chocolate twist',
  'croissant vegan',
  'hazelnut croissant',
  'maple pecan plait',
  'pain au chocolat',
  'chocolate fudge brownie',
  'lemon pistachio',
  'maple pecan pie',
  'morello cherry',
  'pistachio creme',
  'tiramisu blondie',
];

const normalizeMenuName = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const isKasaVenue = (venue, venueId) => {
  const name = normalizeMenuName(venue?.name);
  return name.includes('kasa') || String(venue?.id || venueId || '') === KASA_VENUE_ID;
};

const sortKasaAddOnItems = (items) => {
  const remaining = items.map((item, index) => ({ item, index }));
  const ordered = [];
  KASA_ITEM_ORDER.forEach((key) => {
    const matchIndex = remaining.findIndex(({ item }) => normalizeMenuName(item.name).includes(key));
    if (matchIndex === -1) return;
    ordered.push(remaining[matchIndex].item);
    remaining.splice(matchIndex, 1);
  });
  remaining.sort((a, b) => a.index - b.index);
  return ordered.concat(remaining.map(({ item }) => item));
};

const BookingPage = () => {
  const { venueId: venuePathParam } = useParams();
  const [resolvedVenueId, setResolvedVenueId] = useState(null);
  const [sidebarSubmitSlot, setSidebarSubmitSlot] = useState(null);
  const [venueIdError, setVenueIdError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const routeDiscoveryBundle = location.state?.discoveryBundle ?? null;
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const venueBriefRequestId = searchParams.get('venueBriefRequestId');

  const [venue, setVenue] = useState(null);
  const [venueSpaces, setVenueSpaces] = useState([]);
  const [operatingHours, setOperatingHours] = useState({});
  const [operatingHoursPerPerson, setOperatingHoursPerPerson] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [availabilityPreview, setAvailabilityPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dynamicPricing, setDynamicPricing] = useState([]);
  const [defaultPerPersonPrice, setDefaultPerPersonPrice] = useState(null);
  const [dynamicPricingPerPerson, setDynamicPricingPerPerson] = useState([]);
  const [flatPricing, setFlatPricing] = useState([]);
  const [bookingRules, setBookingRules] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packageQuantities, setPackageQuantities] = useState({});
  const [addOnItems, setAddOnItems] = useState([]);
  const [addOnItemQuantities, setAddOnItemQuantities] = useState({});
  const [otherItems, setOtherItems] = useState([]);
  const [otherItemQuantities, setOtherItemQuantities] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogTags, setCatalogTags] = useState(DEFAULT_CATALOG_ADDON_TAGS);

  const accountContact = organiserContactFromAccount(currentUser, userProfile);

  const [bookingData, setBookingData] = useState({
    name: accountContact?.name || '',
    email: accountContact?.email || '',
    phone: accountContact?.phone || '',
    date: searchParams.get('date') || getYmdInTimezone(new Date(), 'Europe/London'),
    startTime: searchParams.get('startTime') || '18:00',
    endTime: searchParams.get('endTime') || '22:00',
    spaceId: searchParams.get('spaceId') || '',
    bookingType: searchParams.get('bookingType') || resolveDefaultBookingType(null),
    tableCount: searchParams.get('tableCount') ? parseInt(searchParams.get('tableCount'), 10) : 20,
    isFlexible: true,
    isRecurring: false,
    recurringSchedule: '',
    activity: 'Birthday party',
    attendees: '50',
    layout: 'Seating',
    needsAv: false,
    alcohol: 'No',
    catering: 'In-house catering',
    hasDietaryRestrictions: false,
    dietaryRestrictions: '',
    message: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentSetup, setPaymentSetup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountCodeValidation, setDiscountCodeValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');

  const venueId = resolvedVenueId;

  useEffect(() => {
    let cancelled = false;
    setResolvedVenueId(null);
    setVenueIdError(null);

    const syncId =
      resolveVenueIdFromSlugSync(venuePathParam) ||
      (isVenueUuid(venuePathParam) ? String(venuePathParam).trim() : null);

    if (syncId) {
      setResolvedVenueId(syncId);
      return undefined;
    }

    if (!venuePathParam) {
      setVenueIdError('Venue not found');
      return undefined;
    }

    resolveVenueIdFromPathParam(venuePathParam, venueApiService)
      .then((id) => {
        if (cancelled) return;
        if (!id) setVenueIdError('Venue not found');
        else setResolvedVenueId(id);
      })
      .catch((error) => {
        if (!cancelled) setVenueIdError(error.message || 'Venue not found');
      });

    return () => {
      cancelled = true;
    };
  }, [venuePathParam]);

  useEffect(() => {
    const urlDate = searchParams.get('date');
    const urlStartTime = searchParams.get('startTime');
    const urlEndTime = searchParams.get('endTime');
    const urlSpaceId = searchParams.get('spaceId');
    const urlBookingType = searchParams.get('bookingType');
    const urlTableCount = searchParams.get('tableCount');

    if (urlDate || urlStartTime || urlEndTime || urlSpaceId || urlBookingType || urlTableCount) {
      setBookingData((prev) => ({
        ...prev,
        date: urlDate || prev.date,
        startTime: urlStartTime || prev.startTime,
        endTime: urlEndTime || prev.endTime,
        spaceId: urlSpaceId ?? prev.spaceId,
        bookingType: urlBookingType || prev.bookingType,
        tableCount: urlTableCount ? parseInt(urlTableCount, 10) : prev.tableCount
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    setBookingData((prev) => {
      const nextType = coerceBookingTypeForRules(prev.bookingType, bookingRules);
      if (nextType === prev.bookingType) return prev;
      return { ...prev, bookingType: nextType };
    });
  }, [bookingRules]);

  // Sync attendees from previous page: full-venue → venue capacity, per-person → tableCount
  useEffect(() => {
    setBookingData((prev) => {
      const bookingType = prev.bookingType || 'table';
      const tableCount = prev.tableCount ?? 20;
      const attendees =
        bookingType === 'full-venue'
          ? String(venue?.max_capacity ?? venue?.capacity ?? venue?.maxCapacity ?? 50)
          : String(tableCount);
      return { ...prev, attendees };
    });
  }, [venue?.max_capacity, venue?.capacity, venue?.maxCapacity, bookingData.bookingType, bookingData.tableCount]);

  // Cap add-on quantities at 3× attendees
  useEffect(() => {
    const attendees = parseInt(bookingData.attendees, 10);
    if (!Number.isFinite(attendees) || attendees < 1) return;
    const maxQty = attendees * 3;
    setPackageQuantities((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (next[id] > maxQty) {
          next[id] = maxQty;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    setAddOnItemQuantities((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (next[id] > maxQty) {
          next[id] = maxQty;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [bookingData.attendees]);

  useEffect(() => {
    const applyDiscoveryBundle = (bundle) => {
      if (!bundle) return;
      if (bundle.venueData) setVenue(bundle.venueData);
      setDynamicPricing(Array.isArray(bundle.pricing) ? bundle.pricing : []);
      setFlatPricing(Array.isArray(bundle.flatPricingData) ? bundle.flatPricingData : []);
      setBookingRules(bundle.bookingRulesData ?? null);
      setDefaultPerPersonPrice(bundle.defaultPerPersonData?.default_per_person_price ?? null);
      setDynamicPricingPerPerson(
        Array.isArray(bundle.dynamicPerPersonData) ? bundle.dynamicPerPersonData : []
      );
      setVenueSpaces(Array.isArray(bundle.spacesData) ? bundle.spacesData : []);
      setOperatingHours(
        typeof bundle.operatingHoursData === 'object' && bundle.operatingHoursData !== null
          ? bundle.operatingHoursData
          : {}
      );
      setOperatingHoursPerPerson(
        typeof bundle.operatingHoursPerPersonData === 'object' &&
          bundle.operatingHoursPerPersonData !== null
          ? bundle.operatingHoursPerPersonData
          : {}
      );
      setHolidays(Array.isArray(bundle.holidaysData) ? bundle.holidaysData : []);
    };

    const loadVenue = async () => {
      if (!venueId) {
        if (venueIdError) setLoading(false);
        return;
      }

      const cached = getCachedVenueDiscoveryBundle(venueId);
      const routeBundle = venueIdsMatch(routeDiscoveryBundle?.venueData?.id, venueId)
        ? routeDiscoveryBundle
        : null;
      const initialBundle = routeBundle || cached;

      if (initialBundle?.venueData) {
        applyDiscoveryBundle(initialBundle);
        if (routeBundle && !cached) {
          setCachedVenueDiscoveryBundle(venueId, routeBundle);
        }
        setLoading(false);
        void fetchVenueDiscoveryBundle(venueId, { forceRefresh: true })
          .then((freshBundle) => {
            applyDiscoveryBundle(freshBundle);
            setCachedVenueDiscoveryBundle(venueId, freshBundle);
          })
          .catch(() => {});
        return;
      }

      try {
        setLoading(true);
        const bundle = await fetchVenueDiscoveryBundle(venueId, { forceRefresh: true });
        applyDiscoveryBundle(bundle);
        setCachedVenueDiscoveryBundle(venueId, bundle);
      } catch (error) {
        const fallbackVenue = REAL_VENUE_DATA.find((v) => v.id === parseInt(venueId, 10));
        setVenue(fallbackVenue || REAL_VENUE_DATA[0]);
        setDynamicPricing([]);
        setFlatPricing([]);
        setBookingRules(null);
        setDefaultPerPersonPrice(null);
        setDynamicPricingPerPerson([]);
      } finally {
        setLoading(false);
      }
    };

    loadVenue();
  }, [venueId, venueIdError, routeDiscoveryBundle]);

  useEffect(() => {
    let cancelled = false;
    if (!venueId) {
      setPackages([]);
      setAddOnItems([]);
      setOtherItems([]);
      setCatalogLoading(false);
      return undefined;
    }

    const venueIdString = String(venueId);
    setCatalogLoading(true);
    (async () => {
      const [packageResult, addonResult, otherResult] = await Promise.allSettled([
        venueDashboardApiService.getVenuePackages(venueIdString),
        venueDashboardApiService.getVenueAddOnItems(venueIdString, { itemKind: 'addon' }),
        venueDashboardApiService.getVenueAddOnItems(venueIdString, { itemKind: 'other' }),
      ]);
      if (cancelled) return;
      if (packageResult.status === 'fulfilled') {
        setPackages(Array.isArray(packageResult.value) ? packageResult.value : []);
      }
      if (addonResult.status === 'fulfilled') {
        setAddOnItems(Array.isArray(addonResult.value) ? addonResult.value : []);
      }
      if (otherResult.status === 'fulfilled') {
        setOtherItems(Array.isArray(otherResult.value) ? otherResult.value : []);
      }
      setCatalogLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [venueId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tags = await venueDashboardApiService.getCatalogAddonTags();
        if (!cancelled && tags.length > 0) setCatalogTags(tags);
      } catch {
        if (!cancelled) setCatalogTags(DEFAULT_CATALOG_ADDON_TAGS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadSpaces = async () => {
      if (!venueId) {
        setVenueSpaces([]);
        return;
      }

      const bundled = venueIdsMatch(routeDiscoveryBundle?.venueData?.id, venueId)
        ? routeDiscoveryBundle
        : getCachedVenueDiscoveryBundle(venueId);
      if (Array.isArray(bundled?.spacesData)) {
        setVenueSpaces(bundled.spacesData);
        return;
      }

      try {
        const venueIdString = String(venueId);
        const spacesData = await venueDashboardApiService.getVenueSpaces(venueIdString);
        setVenueSpaces(Array.isArray(spacesData) ? spacesData : []);
      } catch {
        setVenueSpaces([]);
      }
    };

    loadSpaces();
  }, [venueId, routeDiscoveryBundle]);

  useEffect(() => {
    const loadOperatingHoursAndHolidays = async () => {
      if (!venueId) {
        setOperatingHours({});
        setOperatingHoursPerPerson({});
        setHolidays([]);
        return;
      }

      const bundled = venueIdsMatch(routeDiscoveryBundle?.venueData?.id, venueId)
        ? routeDiscoveryBundle
        : getCachedVenueDiscoveryBundle(venueId);
      if (bundled) {
        setOperatingHours(
          typeof bundled.operatingHoursData === 'object' && bundled.operatingHoursData !== null
            ? bundled.operatingHoursData
            : {}
        );
        setOperatingHoursPerPerson(
          typeof bundled.operatingHoursPerPersonData === 'object' &&
            bundled.operatingHoursPerPersonData !== null
            ? bundled.operatingHoursPerPersonData
            : {}
        );
        setHolidays(Array.isArray(bundled.holidaysData) ? bundled.holidaysData : []);
        return;
      }

      try {
        const [oh, ohPerPerson, hol] = await Promise.all([
          venueDashboardApiService.getOperatingHours(venueId).catch(() => ({})),
          venueDashboardApiService.getOperatingHoursPerPerson(venueId).catch(() => ({})),
          venueDashboardApiService.getHolidays(venueId).catch(() => [])
        ]);
        setOperatingHours(typeof oh === 'object' && oh !== null && !Array.isArray(oh) ? oh : {});
        setOperatingHoursPerPerson(typeof ohPerPerson === 'object' && ohPerPerson !== null && !Array.isArray(ohPerPerson) ? ohPerPerson : {});
        setHolidays(Array.isArray(hol) ? hol : []);
      } catch {
        setOperatingHours({});
        setOperatingHoursPerPerson({});
        setHolidays([]);
      }
    };
    loadOperatingHoursAndHolidays();
  }, [venueId, routeDiscoveryBundle]);

  // Set default space when spaces load and no spaceId from URL
  useEffect(() => {
    if (venueSpaces.length > 0 && !searchParams.get('spaceId')) {
      const firstSpaceId = venueSpaces[0]?.id || venueSpaces[0]?.space_id || '';
      if (firstSpaceId) {
        setBookingData((prev) => (!prev.spaceId ? { ...prev, spaceId: firstSpaceId } : prev));
      }
    }
  }, [venueSpaces, searchParams]);

  // Fetch space availability + pricing for selected slot
  // Space ID must come from GET /venues/{venue_id}/spaces (never a placeholder)
  useEffect(() => {
    const fetchAvailability = async () => {
      const venueIdStr = venueId ? String(venueId) : null;
      const spaceId = bookingData.spaceId && !String(bookingData.spaceId).includes('{') ? bookingData.spaceId : null;
      const date = bookingData.date;
      const startTime = bookingData.startTime;
      const endTime = bookingData.endTime;
      if (!venueIdStr || !spaceId || !date || !startTime || !endTime) {
        setAvailabilityPreview(null);
        return;
      }
      try {
        const bookingMode = bookingData.bookingType === 'full-venue' ? 'exclusive' : 'per_person';
        const headcount = bookingMode === 'per_person' ? parseInt(bookingData.attendees, 10) || 1 : undefined;
        const venueTimezone = venue?.timezone || venue?.time_zone || 'Europe/London';
        const data = await venueDashboardApiService.getSpaceAvailabilityPreview(
          venueIdStr,
          spaceId,
          {
            date,
            startTime: startTime.length >= 5 ? startTime : `${startTime}:00`,
            endTime: endTime.length >= 5 ? endTime : `${endTime}:00`,
            bookingMode,
            headcount,
            timezone: venueTimezone
          }
        );
        setAvailabilityPreview(data);
      } catch {
        setAvailabilityPreview(null);
      }
    };
    fetchAvailability();
  }, [venueId, venue?.timezone, venue?.time_zone, bookingData.spaceId, bookingData.date, bookingData.startTime, bookingData.endTime, bookingData.bookingType, bookingData.attendees]);

  const normalizePackage = (pkg) => {
    const items =
      (Array.isArray(pkg.items) && pkg.items) ||
      (Array.isArray(pkg.package_items) && pkg.package_items) ||
      (Array.isArray(pkg.packageItems) && pkg.packageItems) ||
      [];

    const rawPrice = pkg.price ?? pkg.price_per_person ?? pkg.pricePerPerson ?? pkg.unit_price ?? pkg.unitPrice;
    const price = typeof rawPrice === 'number' && !Number.isNaN(rawPrice)
      ? rawPrice
      : (typeof rawPrice === 'string' ? parseFloat(rawPrice) : NaN);

    return {
      id: pkg.id || pkg.package_id,
      name: pkg.name || pkg.title || 'Package',
      price: Number.isFinite(price) ? price : 0,
      items,
      tags: normalizeTagSlugs(pkg.tags),
    };
  };

  const getItemQuantity = (item) => item.quantity ?? item.qty ?? item.count ?? 1;
  const getItemPrice = (item) => {
    const raw = item.price_per_unit ?? item.price ?? item.unit_price ?? item.pricePerUnit
      ?? item.item?.price ?? item.item?.price_per_unit ?? item.item?.unit_price;
    const n = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const getPackageUnitPrice = (pkg) => {
    if (Number.isFinite(pkg.price) && pkg.price > 0) {
      return pkg.price;
    }
    const itemList = pkg.items || pkg.package_items || pkg.packageItems || [];
    if (!Array.isArray(itemList) || itemList.length === 0) {
      return 0;
    }
    return itemList.reduce(
      (sum, item) => sum + getItemPrice(item) * getItemQuantity(item),
      0
    );
  };

  const normalizedPackages = useMemo(() => {
    return packages
      .map(normalizePackage)
      .filter((pkg) => pkg.id || pkg.name)
      .map((pkg) => ({
        ...pkg,
        unitPrice: getPackageUnitPrice(pkg)
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getPackageUnitPrice is stable for package list normalization
  }, [packages]);

  const normalizeCatalogItem = (item, { defaultName = 'Add-on', defaultUnit = null } = {}) => ({
    id: item.id || item.item_id,
    name: item.name || item.item_name || item.title || defaultName,
    unitPrice: Number(item.price ?? item.price_per_unit ?? item.unit_price ?? 0),
    pricing_unit: defaultUnit || getAddonPricingUnit(item),
    default_quantity: Number(item.default_quantity ?? item.defaultQuantity ?? 1),
    item_kind: item.item_kind || item.itemKind,
    tags: normalizeTagSlugs(item.tags),
  });

  const normalizedAddOnItems = useMemo(() => {
    const items = (addOnItems || [])
      .filter((item) => item.is_active !== false)
      .map((item) => normalizeCatalogItem(item, { defaultName: 'Add-on' }))
      .filter((item) => item.id);
    return isKasaVenue(venue, venueId) ? sortKasaAddOnItems(items) : items;
  }, [addOnItems, venue, venueId]);

  const normalizedOtherItems = useMemo(() => (
    (otherItems || [])
      .filter((item) => item.is_active !== false)
      .map((item) => normalizeCatalogItem(item, { defaultName: 'Other item' }))
      .filter((item) => item.id)
  ), [otherItems]);

  useEffect(() => {
    if (normalizedAddOnItems.length === 0) return;
    setAddOnItemQuantities((prev) => {
      const allowedIds = new Set(normalizedAddOnItems.map((item) => item.id));
      const next = {};
      Object.entries(prev).forEach(([id, qty]) => {
        if (allowedIds.has(id)) next[id] = qty;
      });
      return next;
    });
  }, [normalizedAddOnItems]);

  useEffect(() => {
    if (normalizedOtherItems.length === 0) return;
    setOtherItemQuantities((prev) => {
      const allowedIds = new Set(normalizedOtherItems.map((item) => item.id));
      const next = {};
      Object.entries(prev).forEach(([id, qty]) => {
        if (allowedIds.has(id)) next[id] = qty;
      });
      return next;
    });
  }, [normalizedOtherItems]);

  useEffect(() => {
    if (normalizedPackages.length === 0) return;
    setPackageQuantities((prev) => {
      const allowedIds = new Set(normalizedPackages.map((pkg) => pkg.id));
      const next = {};
      Object.entries(prev || {}).forEach(([key, value]) => {
        if (allowedIds.has(key) && value > 0) {
          next[key] = value;
        }
      });
      return next;
    });
  }, [normalizedPackages]);

  useEffect(() => {
    const contact = organiserContactFromAccount(currentUser, userProfile);
    if (!contact) return;

    setBookingData((prev) => ({
      ...prev,
      name: contact.name || prev.name,
      email: contact.email || prev.email,
      phone: prev.phone?.trim() ? prev.phone : (contact.phone || prev.phone)
    }));
  }, [currentUser, userProfile]);

  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    let durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    return durationMinutes / 60;
  };

  const calculateDistance = (venueData) => {
    if (!venueData || !venueData.address) return 'Distance not available';

    return getMetroDistanceText(venueData);
  };

  const getActualPrice = (date, startTime, endTime) => {
    if (!venue) {
      return 0;
    }

    if (!date || !dynamicPricing || dynamicPricing.length === 0) {
      const rate = venue.hourly_rate || venue.hourlyRate || venue.price || 0;
      return rate;
    }

    const dateStr = date;
    const baseHourlyRate = venue.hourly_rate || venue.hourlyRate || 0;

    const applicableRules = dynamicPricing.filter((rule) => {
      if (!rule.start_date || !rule.end_date) return false;

      const [sYear, sMonth, sDay] = rule.start_date.split('-').map(Number);
      const [eYear, eMonth, eDay] = rule.end_date.split('-').map(Number);
      const [dYear, dMonth, dDay] = dateStr.split('-').map(Number);

      const startDate = new Date(sYear, sMonth - 1, sDay);
      const endDate = new Date(eYear, eMonth - 1, eDay);
      const selectedDate = new Date(dYear, dMonth - 1, dDay);

      return selectedDate >= startDate && selectedDate <= endDate;
    });

    if (applicableRules.length === 0) {
      return baseHourlyRate;
    }

    const { rate } = resolveFullVenueDynamicHourlyRate(
      applicableRules,
      startTime,
      endTime,
      baseHourlyRate
    );
    return rate;
  };

  const pricingDetails = useMemo(() => {
    const selectedPackageAddOns = normalizedPackages
      .map((pkg) => {
        const quantity = packageQuantities[pkg.id] || 0;
        if (!quantity) return null;
        const unitPrice = pkg.unitPrice ?? getPackageUnitPrice(pkg);
        return {
          id: pkg.id,
          name: pkg.name,
          quantity,
          unitPrice,
          total: unitPrice * quantity,
          itemKind: 'package'
        };
      })
      .filter(Boolean);

    const selectedCatalogAddOns = normalizedAddOnItems
      .map((item) => {
        const quantity = addOnItemQuantities[item.id] || 0;
        if (!quantity) return null;
        return {
          id: item.id,
          name: item.name,
          quantity,
          unitPrice: item.unitPrice,
          total: resolveAddonLineTotal(item.unitPrice, quantity),
          itemKind: 'addon',
          pricing_unit: item.pricing_unit
        };
      })
      .filter(Boolean);

    const selectedOtherAddOns = normalizedOtherItems
      .map((item) => {
        const quantity = otherItemQuantities[item.id] || 0;
        if (!quantity) return null;
        return {
          id: item.id,
          name: item.name,
          quantity,
          unitPrice: item.unitPrice,
          total: resolveAddonLineTotal(item.unitPrice, quantity),
          itemKind: 'other',
          pricing_unit: item.pricing_unit
        };
      })
      .filter(Boolean);

    const selectedAddOns = [
      ...selectedPackageAddOns,
      ...selectedCatalogAddOns,
      ...selectedOtherAddOns,
    ];

    const addOnsTotal = selectedAddOns.reduce((sum, item) => sum + item.total, 0);

    if (!venue) {
      return {
        hourlyRate: 0,
        duration: 0,
        cleaningFee: CLEANING_FEE,
        processingFee: 0,
        platformFee: 0,
        bookingSubtotal: addOnsTotal,
        chargeTotal: addOnsTotal,
        estimatedTotal: addOnsTotal,
        isDiscounted: false,
        baseHourlyRate: 0,
        baseFee: 0,
        addOnsTotal,
        selectedAddOns,
        discountAmount: 0,
        discountCode: null,
        originalTotal: addOnsTotal
      };
    }

    const duration = calculateDuration(bookingData.startTime, bookingData.endTime);
    const isPerPerson = bookingData.bookingType === 'table';
    const tableCount = isPerPerson ? (parseInt(bookingData.attendees, 10) || parseInt(bookingData.tableCount, 10) || 1) : 1;
    const dynamicPricingRate = getActualPrice(
      bookingData.date,
      bookingData.startTime,
      bookingData.endTime
    );
    const baseHourlyRate = venue.hourly_rate || venue.hourlyRate || venue.price || 0;
    const hasReducedRate = dynamicPricingRate < baseHourlyRate;
    const apiEstimatedTotal = availabilityPreview?.estimated_total_amount != null
      ? Number(availabilityPreview.estimated_total_amount)
      : null;
    const effectiveHourlyRate = dynamicPricingRate ?? baseHourlyRate;
    const perPersonRateFromNewApis = resolvePerPersonRateForBooking({
      availabilityPreview,
      date: bookingData.date,
      startTime: bookingData.startTime,
      endTime: bookingData.endTime,
      dynamicPricingPerPerson,
      defaultPerPersonPrice,
      defaultHourlyPrice: effectiveHourlyRate
    });
    const activeFlatRules = (flatPricing || []).filter(r => r.is_active !== false);
    const matchingFlatRule = activeFlatRules
      .filter(r => duration >= (r.min_hours || 0))
      .sort((a, b) => (b.min_hours || 0) - (a.min_hours || 0))[0];
    const flatPriceTotal = matchingFlatRule?.flat_price != null ? Number(matchingFlatRule.flat_price) : null;
    const hasApplicableDynamicRule = dynamicPricing?.length && dynamicPricingRate !== baseHourlyRate;
    const perPersonRate = isPerPerson ? perPersonRateFromNewApis : null;
    let baseFee;
    const fullVenueComputed = effectiveHourlyRate * duration;
    if (isPerPerson) {
      baseFee = perPersonRate != null ? perPersonRate * tableCount * duration : 0;
    } else if (hasApplicableDynamicRule) {
      baseFee = dynamicPricingRate * duration;
    } else if (flatPriceTotal != null) {
      baseFee = flatPriceTotal;
    } else if (hasReducedRate) {
      baseFee = dynamicPricingRate * duration;
    } else {
      // Availability preview `estimated_total_amount` can disagree with venue rate × duration (e.g. wrong
      // semantics or per-space subtotal). Only trust it when it matches computed rent within a few pence.
      if (apiEstimatedTotal != null) {
        const delta = Math.abs(apiEstimatedTotal - fullVenueComputed);
        baseFee = delta <= 0.05 ? apiEstimatedTotal : fullVenueComputed;
      } else {
        baseFee = fullVenueComputed;
      }
    }
    const hourlyRate = isPerPerson ? perPersonRate : effectiveHourlyRate;
    const usingFlatPricing = !isPerPerson && !hasApplicableDynamicRule && flatPriceTotal != null;
    const defaultPerPersonRate =
      defaultPerPersonPrice != null && defaultPerPersonPrice !== ''
        ? Number(defaultPerPersonPrice)
        : null;
    const isDiscounted = isPerPerson
      ? Number.isFinite(defaultPerPersonRate) &&
        defaultPerPersonRate > 0 &&
        Number.isFinite(perPersonRate) &&
        perPersonRate < defaultPerPersonRate
      : effectiveHourlyRate < baseHourlyRate;
    const originalTotal = baseFee + addOnsTotal;
    
    // Apply discount if valid discount code is applied
    let discountAmount = 0;
    let finalTotal = originalTotal;
    
    if (discountCodeValidation && discountCodeValidation.is_valid) {
      discountAmount = discountCodeValidation.discount_amount || 0;
      finalTotal = discountCodeValidation.discounted_total || originalTotal;
    }

    const bookingSubtotal = finalTotal;
    const feeBreakdown = computePaymentFeeBreakdown(bookingSubtotal, {
      isPlatformBooking: true,
      venueId: venue?.id,
      platformFeeEnabled: venue?.platform_fee_enabled !== false
    });

    return {
      hourlyRate,
      duration,
      cleaningFee: CLEANING_FEE,
      processingFee: feeBreakdown.processingFee,
      platformFee: feeBreakdown.platformFee,
      bookingSubtotal,
      chargeTotal: feeBreakdown.chargeTotal,
      estimatedTotal: feeBreakdown.chargeTotal,
      originalTotal,
      isDiscounted,
      baseHourlyRate,
      baseFee,
      addOnsTotal,
      selectedAddOns,
      discountAmount,
      discountCode: discountCodeValidation?.is_valid ? discountCode : null,
      isPerPerson,
      tableCount: isPerPerson ? tableCount : null,
      perPersonRate,
      usingFlatPricing,
      matchingFlatRule: usingFlatPricing ? matchingFlatRule : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pricing helpers are stable for this memo
  }, [
    bookingData.date,
    bookingData.endTime,
    bookingData.startTime,
    bookingData.attendees,
    bookingData.bookingType,
    bookingData.tableCount,
    dynamicPricing,
    defaultPerPersonPrice,
    dynamicPricingPerPerson,
    flatPricing,
    venue,
    availabilityPreview,
    discountCodeValidation,
    normalizedPackages,
    packageQuantities,
    normalizedAddOnItems,
    addOnItemQuantities,
    normalizedOtherItems,
    otherItemQuantities
  ]);

  const metroDistance = useMemo(() => calculateDistance(venue), [venue]);

  // Validate discount code
  const handleValidateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      setDiscountCodeValidation(null);
      return;
    }

    setIsValidatingDiscount(true);
    setDiscountError('');

    try {
      // Use pricingDetails.baseFee (from availability API when available)
      const currentTotal = (pricingDetails.baseFee || 0) + (pricingDetails.addOnsTotal || 0);

      const validation = await discountCodeApiService.validateDiscountCode(
        discountCode.trim().toUpperCase(),
        currentTotal
      );

      if (validation.is_valid) {
        setDiscountCodeValidation(validation);
        setDiscountError('');
      } else {
        setDiscountCodeValidation(null);
        setDiscountError(validation.message || 'Invalid discount code');
      }
    } catch (error) {
      setDiscountCodeValidation(null);
      setDiscountError('Failed to validate discount code. Please try again.');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  // Clear discount code
  const handleClearDiscountCode = () => {
    setDiscountCode('');
    setDiscountCodeValidation(null);
    setDiscountError('');
  };

  usePageSeo({
    title: venue ? `Book ${venue.name}` : 'Complete your booking',
    description: venue
      ? `Confirm date, time, and details for your event at ${venue.name} on Occasia.`
      : undefined,
    path: venueId ? `/booking/${venueId}` : '/booking',
    noindex: true
  });

  if (loading) {
    return (
      <div className="booking-page">
        <div className="booking-container">
          <div className="loading-state">
            <p>Loading venue information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="booking-page">
        <div className="booking-container">
          <div className="error-state">
            <h2>Venue not found</h2>
            <p>The venue you're trying to book could not be found.</p>
            <button type="button" onClick={() => navigate('/')}>Return to Home</button>
          </div>
        </div>
      </div>
    );
  }

  const hidePricing = !shouldShowVenuePricing(venue);
  const stripePaymentsUiEnabled = isStripePaymentsUiEnabled(venueId, venue);

  const formatEuropeanPhone = (value) => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '');
    
    // If it starts with +, keep it
    const hasPlus = cleaned.startsWith('+');
    let digitsOnly = hasPlus ? cleaned.substring(1) : cleaned;
    digitsOnly = digitsOnly.replace(/\D/g, '');
    
    // Limit to max 15 digits
    digitsOnly = digitsOnly.substring(0, 15);
    
    if (digitsOnly.length === 0) {
      return hasPlus ? '+' : '';
    }
    
    // Format with spaces for readability
    if (hasPlus) {
      // Format with country code: +XX XXXX XXXXXX
      // Common European country codes: 1-3 digits
      let formatted = '+';
      
      if (digitsOnly.length <= 3) {
        // Just country code
        formatted += digitsOnly;
      } else if (digitsOnly.length <= 7) {
        // Country code + first part
        formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3);
      } else if (digitsOnly.length <= 10) {
        // Country code + two parts
        formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3, 6) + ' ' + digitsOnly.substring(6);
      } else {
        // Country code + multiple parts
        formatted += digitsOnly.substring(0, 3) + ' ' + digitsOnly.substring(3, 6) + ' ' + digitsOnly.substring(6, 9) + ' ' + digitsOnly.substring(9);
      }
      return formatted;
    } else {
      // Format without country code: XXXX XX XX XX
      if (digitsOnly.length <= 4) {
        return digitsOnly;
      } else if (digitsOnly.length <= 6) {
        return digitsOnly.substring(0, 4) + ' ' + digitsOnly.substring(4);
      } else if (digitsOnly.length <= 8) {
        return digitsOnly.substring(0, 4) + ' ' + digitsOnly.substring(4, 6) + ' ' + digitsOnly.substring(6);
      } else {
        return digitsOnly.substring(0, 4) + ' ' + digitsOnly.substring(4, 6) + ' ' + digitsOnly.substring(6, 8) + ' ' + digitsOnly.substring(8);
      }
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      // Format European phone number as user types
      value = formatEuropeanPhone(value);
    }

    setBookingData((prev) => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'catering' && !String(value || '').toLowerCase().includes('in-house')) {
        next.hasDietaryRestrictions = false;
        next.dietaryRestrictions = '';
      }
      if (field === 'hasDietaryRestrictions' && !value) {
        next.dietaryRestrictions = '';
      }
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setErrors({});
    setSubmitError('');
    setPaymentSetup(null);

    const newErrors = {};
    if (!bookingData.name.trim()) newErrors.name = 'Name is required';
    if (!bookingData.email.trim()) newErrors.email = 'Email is required';
    if (!acceptTerms) newErrors.terms = 'You must accept the host rules and disclosures';

    // Date validation - must be today or a future date in the venue timezone (not the user's browser)
    if (bookingData.date) {
      const venueTz = venue?.timezone || venue?.time_zone || 'Europe/London';
      const todayVenueYmd = getYmdInTimezone(new Date(), venueTz);
      const selectedYmd = String(bookingData.date).split('T')[0];
      if (selectedYmd < todayVenueYmd) {
        newErrors.date = 'Date cannot be in the past';
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (bookingData.email.trim() && !emailRegex.test(bookingData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // European phone number validation
    // Remove all formatting characters to get just digits
    const cleanPhone = bookingData.phone.trim().replace(/[\s\-()]/g, '');
    const phoneDigits = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : cleanPhone;
    
    // European phone numbers should have 9-15 digits (including country code if present)
    // Minimum 9 digits for local numbers, up to 15 with country code
    if (bookingData.phone.trim()) {
      if (phoneDigits.length < 9) {
        newErrors.phone = 'Phone number must have at least 9 digits';
      } else if (phoneDigits.length > 15) {
        newErrors.phone = 'Phone number cannot exceed 15 digits';
      } else if (!/^\+?\d{9,15}$/.test(cleanPhone)) {
        newErrors.phone = 'Please enter a valid European phone number';
      }
    }

    // Minimum hours validation (applies to both full venue and per-person)
    const duration = calculateDuration(bookingData.startTime, bookingData.endTime);
    const isFullVenue = bookingData.bookingType === 'full-venue';
    const minHours = isFullVenue
      ? (bookingRules?.min_hours_exclusive ?? bookingRules?.min_total_hours ?? 2)
      : (bookingRules?.min_hours_per_person ?? 2);
    if (duration < minHours) {
      newErrors.duration = `Minimum booking is ${minHours} hours for ${isFullVenue ? 'full venue hire' : 'per-person'}`;
    }

    if (bookingData.isRecurring && !bookingData.recurringSchedule.trim()) {
      newErrors.recurringSchedule = 'Please describe the recurring schedule or duration';
    }

    const isInHouseCatering = String(bookingData.catering || '')
      .trim()
      .toLowerCase()
      .includes('in-house');
    if (
      isInHouseCatering &&
      bookingData.hasDietaryRestrictions &&
      !bookingData.dietaryRestrictions.trim()
    ) {
      newErrors.dietaryRestrictions = 'Please share dietary restrictions for your guests';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitError('Please fill in all required fields correctly');
      return;
    }

    setIsSubmitting(true);

    try {
      const baseFee = pricingDetails.baseFee ?? (getActualPrice(bookingData.date, bookingData.startTime, bookingData.endTime) * duration);
      const actualPrice = duration > 0 ? baseFee / duration : pricingDetails.hourlyRate ?? getActualPrice(bookingData.date, bookingData.startTime, bookingData.endTime);
      const cleaningFee = CLEANING_FEE;
      const addOnsTotal = pricingDetails.addOnsTotal || 0;
      const originalTotal = baseFee + addOnsTotal;
      
      // Apply discount if valid
      let finalTotal = originalTotal;
      if (discountCodeValidation && discountCodeValidation.is_valid) {
        finalTotal = discountCodeValidation.discounted_total || originalTotal;
      }

      const bookingSubtotal = finalTotal;
      const feeBreakdown = computePaymentFeeBreakdown(bookingSubtotal, {
      isPlatformBooking: true,
      venueId: venue?.id,
      platformFeeEnabled: venue?.platform_fee_enabled !== false
    });
      const processingFee = feeBreakdown.processingFee;
      const platformFee = feeBreakdown.platformFee;
      const estimatedTotal = bookingSubtotal;

      const spreadsheetPayload = {
        name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone,
        message: bookingData.message,
        event_date: bookingData.date,
        event_start_time: bookingData.startTime,
        event_end_time: bookingData.endTime,
        duration_hours: duration,
        space_id: bookingData.spaceId || undefined,
        booking_type: bookingData.bookingType || undefined,
        table_count: bookingData.tableCount ?? undefined,
        activity: bookingData.activity,
        attendees: parseInt(bookingData.attendees, 10),
        layout: bookingData.layout,
        needs_av: Boolean(bookingData.needsAv),
        alcohol_consumption: bookingData.alcohol,
        catering_preference: bookingData.catering,
        ...(String(bookingData.catering || '').toLowerCase().includes('in-house')
          ? {
              has_dietary_restrictions: Boolean(bookingData.hasDietaryRestrictions),
              dietary_restrictions: bookingData.hasDietaryRestrictions
                ? bookingData.dietaryRestrictions.trim()
                : null
            }
          : {}),
        is_flexible: bookingData.isFlexible,
        is_recurring: bookingData.isRecurring,
        recurring_schedule: bookingData.isRecurring ? bookingData.recurringSchedule.trim() : undefined,
        booking_frequency: bookingData.isRecurring ? 'Not sure yet' : 'One-off',
        venue_name: venue.name,
        venue_id: venue.id,
        venue_address: venue.address || venue.area || '',
        venue_contact: venue.contact_name || venue.contactName || venue.contact,
        venue_rating: venue.rating,
        ...(hidePricing
          ? {
              price: 'TBC',
              base_price_per_hour: 'TBC',
              base_fee: 'TBC',
              cleaning_fee: 'TBC',
              processing_fee: 'TBC',
              add_ons_total: 'TBC',
              packages: (pricingDetails.selectedAddOns || []).map((item) => ({
                package_id: item.id,
                name: item.name,
                quantity: item.quantity,
                unit_price: 'TBC',
                total: 'TBC'
              })),
              estimated_total: 'TBC',
              pricing_display: 'TBC'
            }
          : {
              price: estimatedTotal,
              base_price_per_hour: actualPrice,
              base_fee: baseFee,
              cleaning_fee: cleaningFee,
              processing_fee: processingFee,
              platform_fee: platformFee,
              add_ons_total: addOnsTotal,
              packages: pricingDetails.selectedAddOns?.map((item) => ({
                package_id: item.id,
                name: item.name,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                total: item.total
              })),
              estimated_total: estimatedTotal
            }),
        timestamp: new Date().toISOString(),
        source: 'booking_form'
      };

      const bookingReference = `BK-${new Date().getFullYear()}-${String(
        Math.floor(Math.random() * 9999) + 1
      ).padStart(3, '0')}`;

      // Ensure venue_id is a string
      const venueIdString = venue.id ? String(venue.id) : null;
      
      if (!venueIdString) {
        throw new Error('Venue ID is required but not available');
      }

      // Postgres user UUID only — Firebase UIDs are not valid customer_id FKs.
      // For anonymous bookings we intentionally leave customer_id null so venue emails use submitted organiser details.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const pickUuid = (...candidates) => {
        for (const c of candidates) {
          if (c != null && String(c).trim() && UUID_RE.test(String(c).trim())) {
            return String(c).trim();
          }
        }
        return null;
      };
      let backendUserId = pickUuid(
        userProfile?.backend_user_id,
        userProfile?.id
      );
      const storedUser = localStorage.getItem('user');
      if (storedUser && !backendUserId) {
        try {
          const parsedUser = JSON.parse(storedUser);
          backendUserId = pickUuid(parsedUser.id);
        } catch (e) { /* ignore */ }
      }
      const customerIdForBooking = backendUserId || null;

      // Denormalised contact for API / venue emails (must match form even when customer_id is guest)
      const trimmedName = (bookingData.name || '').trim();
      const trimmedEmail = (bookingData.email || '').trim();
      const trimmedPhone = (bookingData.phone || '').trim();

      // Build booking payload - start with required fields from API docs
      const bookingPayload = {
        booking_reference: bookingReference,
        venue_id: venueIdString,
        event_name: bookingData.activity,
        activity: bookingData.activity,
        // total_amount should be the final amount (before discount if discount_code is used)
        // The backend will apply discount and calculate final_amount
        total_amount: hidePricing ? 0 : originalTotal, // Total before discount
        tax_amount: 0
      };
      if (customerIdForBooking) {
        bookingPayload.customer_id = customerIdForBooking;
      }

      // Add optional fields that might be expected
      if (searchParams.get('eventType')) {
        bookingPayload.event_type = searchParams.get('eventType');
      }
      const isPerPersonBooking = bookingData.bookingType === 'table';
      bookingPayload.booking_mode = isPerPersonBooking ? 'per_person' : 'exclusive';
      if (isPerPersonBooking) {
        bookingPayload.headcount = parseInt(bookingData.attendees, 10) || parseInt(bookingData.tableCount, 10) || 1;
      }
      bookingPayload.booking_date = bookingData.date;
      bookingPayload.start_time = bookingData.startTime;
      bookingPayload.end_time = bookingData.endTime;
      if (bookingData.spaceId) bookingPayload.space_id = bookingData.spaceId;
      if (bookingData.bookingType) bookingPayload.booking_type = bookingData.bookingType;
      bookingPayload.duration_hours = duration;
      bookingPayload.hourly_rate = hidePricing
        ? 0
        : isPerPersonBooking
          ? pricingDetails.perPersonRate ?? actualPrice
          : pricingDetails.hourlyRate ?? actualPrice;
      bookingPayload.final_amount = hidePricing ? 0 : feeBreakdown.chargeTotal;
      bookingPayload.booking_amount = hidePricing ? 0 : bookingSubtotal;
      if (!hidePricing) {
        bookingPayload.fee_breakdown = buildFeeBreakdownSnapshot({
          spaceFee: baseFee,
          addOnsTotal,
          discountAmount: Number(Math.max(0, originalTotal - bookingSubtotal).toFixed(2)),
          chargeableVenue: bookingSubtotal,
          isPlatformBooking: true,
          venueId: venue?.id
        });
      }
      bookingPayload.status = 'pending';
      bookingPayload.payment_status = 'pending';
      if (trimmedName) bookingPayload.customer_name = trimmedName;
      if (trimmedEmail) bookingPayload.customer_email = trimmedEmail;
      if (trimmedPhone) bookingPayload.customer_phone = trimmedPhone;
      if (venueBriefRequestId) bookingPayload.venue_brief_request_id = venueBriefRequestId;
      
      // Send requirements as an object, not a stringified JSON
      // The backend should handle JSON serialization
      bookingPayload.requirements = {
        activity: bookingData.activity,
        layout: bookingData.layout,
        needs_av: Boolean(bookingData.needsAv),
        alcohol_consumption: bookingData.alcohol,
        catering_preference: bookingData.catering,
        ...(String(bookingData.catering || '').toLowerCase().includes('in-house')
          ? {
              has_dietary_restrictions: Boolean(bookingData.hasDietaryRestrictions),
              dietary_restrictions: bookingData.hasDietaryRestrictions
                ? bookingData.dietaryRestrictions.trim()
                : null
            }
          : {}),
        is_flexible: bookingData.isFlexible,
        is_recurring: bookingData.isRecurring,
        recurring_schedule: bookingData.isRecurring ? bookingData.recurringSchedule.trim() : undefined,
        booking_frequency: bookingData.isRecurring ? 'Not sure yet' : 'One-off',
        space_id: bookingData.spaceId || undefined,
        booking_type: bookingData.bookingType || undefined,
        pricing_mode: isPerPersonBooking ? 'per_person' : 'exclusive',
        per_person_rate: hidePricing
          ? 0
          : isPerPersonBooking
            ? pricingDetails.perPersonRate ?? null
            : null,
        space_hourly_rate: hidePricing
          ? 0
          : !isPerPersonBooking
            ? pricingDetails.hourlyRate ?? null
            : null,
        booking_amount: hidePricing ? 0 : bookingSubtotal,
        add_ons_total: hidePricing ? 0 : addOnsTotal,
        base_fee: hidePricing ? 0 : baseFee,
        duration_hours: duration,
        special_requirements: bookingData.message || '',
        customer_info: {
          name: bookingData.name,
          email: bookingData.email,
          phone: bookingData.phone
        },
        discounted_rate: hidePricing
          ? false
          : actualPrice < (venue.hourly_rate || venue.hourlyRate || venue.price || 0),
        base_hourly_rate: hidePricing ? 0 : venue.hourly_rate || venue.hourlyRate || venue.price || 0,
        processing_fee: hidePricing ? 0 : processingFee,
        platform_fee: hidePricing ? 0 : platformFee,
        submitted_at: new Date().toISOString(),
        source: 'booking_form',
        ...(hidePricing
          ? {
              hide_venue_pricing: true,
              pricing_display: 'TBC'
            }
          : {})
      };
      if (customerIdForBooking) {
        bookingPayload.requirements.customer_id = customerIdForBooking;
      }

      const packageLinesForRequirements = (pricingDetails.selectedAddOns || []).map((item) => ({
        package_id: item.id,
        name: item.name,
        quantity: item.quantity,
        unit_price: hidePricing ? 0 : item.unitPrice,
        total: hidePricing ? 0 : item.total,
        amount: hidePricing ? 0 : item.total,
        item_kind: item.itemKind || 'package',
      }));

      if (packageLinesForRequirements.length) {
        bookingPayload.requirements.packages = packageLinesForRequirements;
      }

      if (pricingDetails.selectedAddOns?.length) {
        bookingPayload.packages = pricingDetails.selectedAddOns.map((item) => ({
          package_id: item.id,
          name: item.name,
          quantity: item.quantity,
          unit_price: hidePricing ? 0 : item.unitPrice,
          amount: hidePricing ? 0 : item.total
        }));
      }

      // Only include discount_code if it's valid (discount UI hidden when pricing is hidden)
      if (!hidePricing && discountCodeValidation?.is_valid && discountCode.trim()) {
        bookingPayload.discount_code = discountCode.trim().toUpperCase();
      }

      // Log the full request details for debugging
      const bookingUrl = `${API_BASE_URL}/bookings`;
      const bookingHeaders = {
        'Content-Type': 'application/json'
      };
      const jwtToken = localStorage.getItem('access_token');
      if (jwtToken) {
        bookingHeaders['Authorization'] = `Bearer ${jwtToken}`;
      }
      const bookingBody = JSON.stringify(bookingPayload);
      

      const [spreadsheetResponse, bookingResponse] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/forms/venue-enquiry`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(spreadsheetPayload)
        }),
        fetch(bookingUrl, {
          method: 'POST',
          headers: bookingHeaders,
          body: bookingBody
        })
      ]);

      let spreadsheetResponseBody = null;
      let bookingResponseBody = null;

      if (spreadsheetResponse.status === 'fulfilled') {
        try {
          const responseClone = spreadsheetResponse.value.clone();
          spreadsheetResponseBody = await responseClone.text();

          if (!spreadsheetResponse.value.ok) {

          }

          try {
            const parsedBody = JSON.parse(spreadsheetResponseBody);
            if (parsedBody.success === false) {
            }
          } catch (jsonError) {
            // non json
          }
        } catch (responseError) {
        }
      } else if (spreadsheetResponse.status === 'rejected') {
      }

      if (bookingResponse.status === 'fulfilled') {
        try {
          const responseClone = bookingResponse.value.clone();
          bookingResponseBody = await responseClone.text();

          // If the bookings API failed, log detailed diagnostics to help debugging
          if (!bookingResponse.value.ok) {


            const status = bookingResponse.value.status;
            if (status === 400) {
              setSubmitError('Please check all fields are filled correctly and try again.');
            } else if (status === 401 || status === 403) {
              setSubmitError('You need to log in to complete this booking.');
            } else if (status === 404) {
              setSubmitError('The venue or space was not found. Please try again.');
            } else if (status >= 500) {
              setSubmitError('The server encountered an error. Please try again in a moment.');
            } else {
              setSubmitError('Booking submission failed. Please try again or contact support.');
            }
          }
        } catch (responseError) {
          setSubmitError('Error processing booking response. Please try again.');
        }
      } else if (bookingResponse.status === 'rejected') {
        setSubmitError('Failed to submit booking. Please check your connection and try again.');
      }

      // Check status codes - only show success modal if status is 200
      let bookingStatusCode = null;

      if (bookingResponse.status === 'fulfilled') {
        bookingStatusCode = bookingResponse.value.status;
      }

      // Only show success modal if booking API returns 200 status
      if (bookingStatusCode === 200) {
        let bookingResult = null;
        let createdBookingId = null;
        try {
          bookingResult = bookingResponseBody ? JSON.parse(bookingResponseBody) : null;
          createdBookingId =
            bookingResult?.id ||
            bookingResult?.booking_id ||
            bookingResult?.data?.id ||
            bookingResult?.data?.booking_id ||
            null;
        } catch (e) {
          bookingResult = null;
          createdBookingId = null;
        }

        const depositRequired = Boolean(
          !hidePricing &&
            stripePaymentsUiEnabled &&
            venue?.deposit_enabled &&
            Number(venue?.deposit_percent) > 0
        );

        if (depositRequired && createdBookingId) {
          setPaymentSetup(null);
          setIsSubmitting(false);
          navigate(
            `/profile/bookings/${encodeURIComponent(String(createdBookingId))}?pay=stripe&from=booking`,
            { replace: true }
          );
          return;
        }

        if (!hidePricing && stripePaymentsUiEnabled) {
          const paymentSetupState = {
            showPrompt: true,
            depositRequired: false,
            bookingId: createdBookingId,
            payUrl: createdBookingId
              ? `/profile/bookings/${encodeURIComponent(String(createdBookingId))}?pay=stripe`
              : null,
            actionUrl: null
          };
          if (createdBookingId) {
            try {
              const setup = await paymentsApiService.createPaymentSetupSession(createdBookingId);
              paymentSetupState.actionUrl =
                setup?.url ||
                setup?.setup_url ||
                setup?.hosted_url ||
                setup?.payment_setup_url ||
                null;
            } catch {
              // Payments setup endpoint can be deployed later.
            }
          }
          setPaymentSetup(paymentSetupState);
        } else {
          setPaymentSetup(null);
        }

        // Customer enquiry is seeded into the booking message thread by POST /bookings.

        setShowSuccessModal(true);
        setIsSubmitting(false);
      } else {
        // Reset button state and show error message
        setIsSubmitting(false);
        setSubmitError('Booking submission failed. Please try again or contact support.');
      }
    } catch (error) {
      setIsSubmitting(false);
      const msg = error.message || '';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setSubmitError('Unable to reach the server. Please check your connection and try again.');
      } else {
        setSubmitError('An unexpected error occurred. Please try again or contact support.');
      }
    }
  };

  return (
    <div className="booking-page">
      {!currentUser ? (
        <div className="booking-login-banner">
          <div className="booking-login-banner-inner">
            <p className="booking-login-banner-message">
              <img
                className="booking-login-banner-icon"
                src="/manual-booking/icons/banner-user.png"
                alt=""
                aria-hidden="true"
                width="18"
                height="18"
              />
              <span>
                <button
                  type="button"
                  className="login-link-button"
                  onClick={() => window.dispatchEvent(new CustomEvent('openLoginModal'))}
                >
                  Sign in
                </button>{' '}
                to auto-fill your details and save time on future bookings.
              </span>
            </p>
          </div>
        </div>
      ) : null}
      <div className="booking-content">
        <div className="booking-form-column">
          <header className="booking-page-heading">
            <h1 className="booking-title">Request to book</h1>
            <p className="booking-intro">
              {stripePaymentsUiEnabled &&
              venue?.deposit_enabled &&
              Number(venue?.deposit_percent) > 0
                ? 'Share your event details with the venue. You’ll pay a deposit with Stripe right after submitting; we send your inquiry once that’s complete.'
                : 'Share your event details with the venue. You won&apos;t be charged until the host confirms.'}
            </p>
          </header>
          <BookingForm
            acceptTerms={acceptTerms}
            availabilityPreview={availabilityPreview}
            bookingData={bookingData}
            currentUser={currentUser}
            errors={errors}
            isSubmitting={isSubmitting}
            onAcceptTermsChange={setAcceptTerms}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
            pricingDetails={pricingDetails}
            submitError={submitError}
            venue={venue}
            venueSpaces={venueSpaces}
            packages={normalizedPackages}
            packageQuantities={packageQuantities}
            onPackageQuantityChange={(packageId, quantity) => {
              const attendees = parseInt(bookingData.attendees, 10) || 0;
              const maxQty = Math.max(0, attendees * 3);
              setPackageQuantities((prev) => ({
                ...prev,
                [packageId]: Math.min(Math.max(0, quantity), maxQty)
              }));
            }}
            addOnItems={normalizedAddOnItems}
            addOnItemQuantities={addOnItemQuantities}
            onAddOnQuantityChange={(itemId, quantity) => {
              const attendees = parseInt(bookingData.attendees, 10) || 0;
              const maxQty = Math.max(0, attendees * 3);
              setAddOnItemQuantities((prev) => ({
                ...prev,
                [itemId]: Math.min(Math.max(0, quantity), maxQty)
              }));
            }}
            otherItems={normalizedOtherItems}
            otherItemQuantities={otherItemQuantities}
            onOtherQuantityChange={(itemId, quantity) => {
              setOtherItemQuantities((prev) => ({
                ...prev,
                [itemId]: Math.max(0, quantity)
              }));
            }}
            bookingRules={bookingRules}
            operatingHours={operatingHours}
            operatingHoursPerPerson={operatingHoursPerPerson}
            holidays={holidays}
            hidePrice={!shouldShowVenuePricing(venue)}
            dynamicPricing={dynamicPricing}
            dynamicPricingPerPerson={dynamicPricingPerPerson}
            defaultPerPersonPrice={defaultPerPersonPrice}
            flatPricing={flatPricing}
            catalogTags={catalogTags}
            catalogLoading={catalogLoading}
            submitSlot={sidebarSubmitSlot}
            stripePaymentsUiEnabled={stripePaymentsUiEnabled}
          />
        </div>
        <VenueSidebar
          submitSlotRef={setSidebarSubmitSlot}
          metroDistance={metroDistance}
          pricingDetails={pricingDetails}
          venue={venue}
          hidePrice={!shouldShowVenuePricing(venue)}
          stripePaymentsUiEnabled={stripePaymentsUiEnabled}
          discountCode={discountCode}
          discountCodeValidation={discountCodeValidation}
          discountError={discountError}
          isValidatingDiscount={isValidatingDiscount}
          onDiscountCodeChange={setDiscountCode}
          onValidateDiscountCode={handleValidateDiscountCode}
          onClearDiscountCode={handleClearDiscountCode}
        />
      </div>
      {showSuccessModal && (
        <SuccessModal
          bookingData={bookingData}
          onClose={() => {
            setShowSuccessModal(false);
            setIsSubmitting(false);
            setPaymentSetup(null);
          }}
          onExploreMore={() => navigate('/search-results')}
          paymentSetup={paymentSetup}
          pricingDetails={pricingDetails}
          venue={venue}
        />
      )}
    </div>
  );
};

export default BookingPage;



